import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import {
  Layers, IndianRupee, Boxes, Shirt, Puzzle, Send, Inbox, AlertTriangle,
  PackageCheck, TrendingUp, ShoppingCart, Receipt, ClipboardList, FileText,
} from "lucide-react";
import { useData } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, KpiCard, Card, Button, StatusBadge } from "../components/shared";
import { money, num, fmtDate } from "../lib/api";

export default function Dashboard() {
  const { data, loading } = useData("/dashboard");
  const navigate = useNavigate();

  if (loading || !data) return <Loading />;
  const k = data.kpi;

  const bigTabs = [
    { label: "Purchase Orders", to: "/purchase-orders", icon: ShoppingCart, color: "bg-blue-800" },
    { label: "WIP", to: "/wip", icon: Boxes, color: "bg-emerald-700" },
    { label: "Sales Order", to: "/sales-orders", icon: Receipt, color: "bg-violet-700" },
  ];

  return (
    <div>
      <PageHeader title="Executive Dashboard" subtitle="ASV Apparels — live production & stock overview" />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {bigTabs.map((t) => (
          <button
            key={t.label}
            data-testid={`bigtab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => navigate(t.to)}
            className={`${t.color} text-white rounded-xl p-5 flex items-center justify-between shadow-sm hover:brightness-110 transition-all`}
          >
            <div className="text-left">
              <p className="text-xs uppercase tracking-wider opacity-80">Go to</p>
              <p className="text-xl font-bold font-head">{t.label}</p>
            </div>
            <t.icon size={30} className="opacity-90" />
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="kpi-wip-pcs" label="WIP Pieces" value={num(k.wip_pcs)} sub={`${k.designs} designs`} icon={Boxes} accent="sky" onClick={() => navigate("/wip")} />
        <KpiCard testid="kpi-wip-value" label="WIP Value" value={money(k.wip_value)} icon={TrendingUp} accent="violet" onClick={() => navigate("/wip")} />
        <KpiCard testid="kpi-acc-value" label="Accessories Value" value={money(k.acc_value)} icon={Puzzle} accent="amber" onClick={() => navigate("/accessories")} />
        <KpiCard testid="kpi-in-jobwork" label="In Job Work" value={num(k.in_jobber)} sub="pcs with jobbers" icon={Send} accent="sky" onClick={() => navigate("/challans")} />
        <KpiCard testid="kpi-pending-jobber" label="Pending Jobber Receipts" value={num(k.pending_jobber_receipts)} icon={Inbox} accent="amber" onClick={() => navigate("/challans")} />
        <KpiCard testid="kpi-short-qty" label="Short Quantity" value={num(k.short_qty)} sub="pcs" icon={AlertTriangle} accent="red" onClick={() => navigate("/wip")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2 p-5">
          <p className="text-sm font-semibold text-slate-800 mb-4 font-head">WIP by Process Stage</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.wip_chart} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="process" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-30} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="pieces" fill="#1E40AF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <p className="text-sm font-semibold text-slate-800 mb-3 font-head flex items-center gap-2">
            <AlertTriangle size={15} className="text-amber-500" /> Alerts
          </p>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {data.alerts.length === 0 && <p className="text-sm text-slate-400">No active alerts</p>}
            {data.alerts.map((a, i) => (
              <div key={i} className={`text-xs px-3 py-2 rounded-md border ${a.type === "danger" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {a.msg}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <RecentCard title="Recent Fabric Purchases" icon={ShoppingCart} rows={data.recent_purchases}
          onClick={() => navigate("/purchase-orders")}
          render={(r) => ({ a: r.code, b: r.supplier_name, c: r.status })} />
        <RecentCard title="Recent Production" icon={ClipboardList} rows={data.recent_production}
          onClick={(r) => navigate(`/production-orders/${r.id}`)}
          render={(r) => ({ a: r.code, b: [r.design_number, r.colour].filter(Boolean).join(" · "), c: r.status })} />
        <RecentCard title="Recent Challans" icon={FileText} rows={data.recent_challans}
          onClick={(r) => navigate(`/challans/${r.challan_number}`)}
          render={(r) => ({ a: r.challan_number, b: r.jobber_name, c: r.status })} />
        <RecentCard title="Recent Jobber Receipts" icon={Inbox} rows={data.recent_receipts}
          onClick={(r) => navigate(`/challans/${r.challan_number}`)}
          render={(r) => ({ a: r.challan_number, b: r.jobber_name, c: `Rec ${num(r.received_qty)}` })} />
        <RecentCard title="Recent Cleared Lots" icon={Shirt} rows={data.recent_clearances}
          onClick={() => navigate("/wip")}
          render={(r) => ({ a: r.slip_number, b: `${r.design_number} · ${r.production_code}`, c: `${num(r.quantity)} pcs` })} />
      </div>
    </div>
  );
}

function RecentCard({ title, icon: Icon, rows, render, onClick }) {
  return (
    <Card className="p-4">
      <p className="text-sm font-semibold text-slate-800 mb-2 font-head flex items-center gap-2">
        <Icon size={15} className="text-blue-700" /> {title}
      </p>
      <div className="divide-y divide-slate-100">
        {(!rows || !rows.length) && <p className="text-sm text-slate-400 py-2">No records</p>}
        {rows?.map((r, i) => {
          const v = render(r);
          return (
            <div key={i} onClick={() => onClick && onClick(r)}
              className={`flex items-center justify-between py-2 ${onClick ? "cursor-pointer hover:bg-slate-50 -mx-1 px-1 rounded" : ""}`}>
              <div>
                <p className="text-sm font-medium text-slate-700">{v.a}</p>
                <p className="text-xs text-slate-400">{v.b}</p>
              </div>
              <span className="text-xs text-slate-500">{v.c}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Loading() {
  return (
    <div className="grid place-items-center h-96">
      <div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
